import { Entitlements } from '@experiences/constants';
import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { useSnackbar } from '@experiences/util';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApAlertBar,
    ApLink,
    ApTab,
    ApTabs,
    ApToggleSwitch,
    PortalDivider,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import { OutcomeBasedBillingLink } from '../../common/constants/documentation/DocumentationLinksDefault';
import { useDocumentationLinks } from '../../common/hooks/useDocumentationLink';
import {
    getFolderFltConfiguration,
    setFolderFltConfiguration,
} from '../../services/licensing/FolderConfigurationService';
import type { Folder } from '../../services/orchestrator/OrchFolderService';
import { accountGlobalId } from '../../store/selectors';
import { OrchestratorFolderTreeView } from '../common/OrchestratorFolderTreeView/OrchestratorFolderTreeView';
import { useEntitlements } from '../entitlements/EntitlementsContextProvider';
import { useCanManageLicensingData } from './useCanManageLicensingData';

const FLT_TAB = 'flt';

const useStyles = makeStyles(theme =>
    createStyles({
        designationCard: {
            border: `1px solid ${theme.palette.semantic.colorBorder}`,
            borderRadius: theme.shape.borderRadius,
        },
        deEmphasized: { color: theme.palette.semantic.colorForegroundDeEmp },
        folderIcon: { color: theme.palette.semantic.colorForeground },
    }),
);

interface ILicensesSettingsPanelProps {
    tenantId: string;
}

export const LicensesSettingsPanel: React.FC<ILicensesSettingsPanelProps> = ({ tenantId }) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const organizationId = useSelector(accountGlobalId);
    const { hasEntitlement } = useEntitlements();
    const { enqueueSnackbar } = useSnackbar();
    const canManageLicensingData = useCanManageLicensingData();
    const getLocalizedLink = useDocumentationLinks();

    const [ selectedFolder, setSelectedFolder ] = useState<Folder>();
    const [ isSaving, setIsSaving ] = useState(false);

    const isFltEligible = hasEntitlement(Entitlements.FltEligible);
    const folderKey = selectedFolder?.IsSelectable ? selectedFolder.Key : '';

    const renderDocsLink = useCallback((chunks: React.ReactNode) => (
        <ApLink
            href={getLocalizedLink({ articleSlug: OutcomeBasedBillingLink })}
            target="_blank"
            rel="noreferrer">
            {chunks}
        </ApLink>
    ), [ getLocalizedLink ]);

    const {
        data: fltConfiguration, isValidating, mutate,
    } = useSWR(
        tenantId && folderKey ? [ 'flt-folder-configuration', organizationId, tenantId, folderKey ] : undefined,
        () => getFolderFltConfiguration(tenantId, folderKey),
    );

    const isFltEnabled = fltConfiguration?.isFltEnabled ?? false;

    const handleToggle = useCallback(async (newValue: boolean) => {
        if (!folderKey) {
            return;
        }
        setIsSaving(true);
        try {
            const updated = await setFolderFltConfiguration(tenantId, folderKey, newValue);
            await mutate(updated, { revalidate: false });
            enqueueSnackbar(translate({ id: 'CLIENT_FLT_SETTING_UPDATED' }), { variant: 'success' });
        } catch {
            enqueueSnackbar(translate({ id: 'CLIENT_FLT_SETTING_UPDATE_FAILED' }), { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    }, [ folderKey, tenantId, mutate, enqueueSnackbar, translate ]);

    const renderFolderConfiguration = () => (
        <UiStack
            direction="column"
            gap={SpacingToken.M}>
            <UiStack
                direction="row"
                align="center"
                gap={SpacingToken.XS}>
                <FolderOutlinedIcon className={classes.folderIcon} />
                <UiText
                    variant={FontVariantToken.fontSizeLBold}
                    data-cy="flt-folder-name">
                    {selectedFolder?.DisplayName}
                </UiText>
            </UiStack>

            <ApTabs
                value={FLT_TAB}
                onValueChanged={() => undefined}>
                <ApTab
                    value={FLT_TAB}
                    label={translate({ id: 'CLIENT_FLT_TAB' })}
                    badge={translate({ id: 'CLIENT_NEW_BADGE' })}
                    data-cy="flt-config-tab"
                />
            </ApTabs>

            {!isFltEligible ? (
                <ApAlertBar
                    status="warning"
                    cancelable={false}
                    data-cy="flt-not-eligible">
                    <UiText>
                        {translate({ id: 'CLIENT_FLT_NOT_ELIGIBLE' }, { a: renderDocsLink })}
                    </UiText>
                </ApAlertBar>
            ) : (
                <>
                    <ApAlertBar
                        status="info"
                        cancelable={false}>
                        <UiText>
                            {translate({ id: 'CLIENT_FLT_INFO_BANNER' }, { a: renderDocsLink })}
                        </UiText>
                    </ApAlertBar>

                    <UiStack
                        direction="column"
                        gap={SpacingToken.S}
                        p={SpacingToken.M}
                        className={classes.designationCard}>
                        <UiStack
                            direction="column"
                            gap={SpacingToken.Micro}>
                            <UiText variant={FontVariantToken.fontSizeMBold}>
                                {translate({ id: 'CLIENT_FLT_DESIGNATION_TITLE' })}
                            </UiText>
                            <UiText
                                variant={FontVariantToken.fontSizeS}
                                className={classes.deEmphasized}>
                                {translate({ id: 'CLIENT_FLT_DESIGNATION_DESCRIPTION' })}
                            </UiText>
                        </UiStack>
                        <UiStack
                            direction="column"
                            gap={SpacingToken.Micro}>
                            <ApToggleSwitch
                                checked={isFltEnabled}
                                disabled={isValidating || isSaving || !canManageLicensingData}
                                label={translate({ id: 'CLIENT_FLT_MARK_AS_FLT' })}
                                onValueChanged={(e) => handleToggle(e.detail)}
                                data-cy="flt-toggle"
                            />
                            <UiText
                                variant={FontVariantToken.fontSizeS}
                                className={classes.deEmphasized}>
                                {translate({ id: isFltEnabled ? 'CLIENT_FLT_MARK_AS_FLT_ACTIVE' : 'CLIENT_FLT_MARK_AS_FLT_INACTIVE' })}
                            </UiText>
                        </UiStack>
                        <UiStack
                            direction="row"
                            align="center"
                            gap={SpacingToken.XS}>
                            <InfoOutlinedIcon
                                className={classes.deEmphasized}
                                fontSize="small" />
                            <UiText
                                variant={FontVariantToken.fontSizeS}
                                className={classes.deEmphasized}
                                data-cy="flt-subfolder-inheritance">
                                {translate(
                                    { id: 'CLIENT_FLT_SUBFOLDER_INHERITANCE' },
                                    {
                                        0: <b>
                                            {selectedFolder?.DisplayName}
                                        </b>,
                                    },
                                )}
                            </UiText>
                        </UiStack>
                    </UiStack>
                </>
            )}
        </UiStack>
    );

    return (
        <UiStack
            h="100%"
            direction="column"
            gap={SpacingToken.S}
            data-cy="licenses-settings-panel">
            <UiStack
                direction="row"
                grow={1}
                gap={SpacingToken.M}
                pt={SpacingToken.Base}
                style={{ minHeight: '80vh' }}>
                <UiStack
                    flex={1}
                    h="100%"
                    gap={SpacingToken.S}
                    style={{
                        minWidth: 0,
                        overflowY: 'auto',
                    }}
                    direction="column">
                    <OrchestratorFolderTreeView
                        organizationId={organizationId}
                        tenantId={tenantId}
                        onChange={setSelectedFolder}
                    />
                </UiStack>
                <PortalDivider customStyles={{ height: '100%' }} />
                <UiStack
                    direction="column"
                    flex={3}
                    gap={SpacingToken.S}
                    style={{ minWidth: 0 }}>
                    {!folderKey ? (
                        <UiText data-cy="flt-no-folder-selected">
                            {translate({ id: 'CLIENT_FLT_SELECT_FOLDER' })}
                        </UiText>
                    ) : (
                        renderFolderConfiguration()
                    )}
                </UiStack>
            </UiStack>
        </UiStack>
    );
};
